/**
 * Journal d'activité NR2J — libellés FR + présentation (UI only).
 */

export const NR2J_ACTIVITY_ACTION_LABELS = Object.freeze({
  TASK_CREATED: 'Tâche créée',
  TASK_COMPLETED: 'Tâche terminée',
  TASK_UPDATED: 'Tâche mise à jour',
  TASK_RESCHEDULED: 'Tâche reportée',
  QUOTE_CREATED: 'Devis créé',
  QUOTE_SENT: 'Devis envoyé',
  QUOTE_ACCEPTED: 'Acceptation client enregistrée',
  QUOTE_ACCEPTED_BY_CLIENT: 'Devis accepté par le client',
  QUOTE_REFUSED: 'Refus client enregistré',
  QUOTE_REFUSED_BY_CLIENT: 'Devis refusé par le client',
  QUOTE_DELETED: 'Devis supprimé',
  QUOTE_APPROVAL_CANCELLED: 'Validation annulée',
  QUOTE_VERSION_CREATED: 'Nouvelle version de devis',
  QUOTE_CANCELLED: 'Devis annulé',
  QUOTE_SUBMITTED: 'Devis soumis à validation',
  QUOTE_APPROVED: 'Devis validé',
  QUOTE_REJECTED: 'Validation interne refusée',
  QUOTE_CHANGES_REQUESTED: 'Modifications demandées',
  INVOICE_CREATED: 'Facture créée',
  INVOICE_ISSUED: 'Facture émise',
  INVOICE_SENT: 'Facture envoyée',
  INVOICE_PAYMENT_ADDED: 'Paiement enregistré',
  INVOICE_DELETED: 'Facture supprimée',
  INVOICE_SUBMITTED: 'Facture soumise à validation',
  INVOICE_APPROVED: 'Facture validée',
  INVOICE_REJECTED: 'Facture refusée',
  INVOICE_CHANGES_REQUESTED: 'Modifications demandées',
  CREDIT_NOTE_CREATED: 'Avoir créé',
  CREDIT_NOTE_ISSUED: 'Avoir émis',
  CREDIT_NOTE_SUBMITTED: 'Avoir soumis à validation',
  CREDIT_NOTE_APPROVED: 'Avoir validé',
  CREDIT_NOTE_REJECTED: 'Avoir refusé',
  CREDIT_NOTE_CHANGES_REQUESTED: 'Modifications demandées',
  PROSPECT_CREATED: 'Prospect créé',
  PROSPECT_STAGE_CHANGED: 'Étape pipeline modifiée',
  ORGANIZATION_CREATED: 'Client créé',
  ORGANIZATION_ARCHIVED: 'Dossier archivé',
  ORGANIZATION_REACTIVATED: 'Dossier réactivé',
  CLIENT_CONVERTED: 'Client créé',
  CLIENT_CREATED: 'Client créé',
  CONTACT_ADDED: 'Contact ajouté',
  CONTACT_UPDATED: 'Contact mis à jour',
  COMMERCANT_LINKED: 'Commerçant lié',
  COMMERCANT_UNLINKED: 'Commerçant délié',
  STATUS_CHANGED: 'Statut modifié',
  NOTE_UPDATED: 'Note mise à jour',
  ORG_CREATED: 'Prospect créé',
  SEED: 'Initialisation',
  SENT: 'Envoyé',
  REJECTED: 'Refusé',
  APPROVED: 'Validé',
  CHANGES_REQUESTED: 'Modifications demandées',
});

/** Filtres « Type d'action » (valeurs métier regroupées). */
export const NR2J_ACTIVITY_TYPE_FILTERS = Object.freeze([
  { id: '', label: 'Toutes les actions' },
  { id: 'TASKS', label: 'Tâches', match: (code) => String(code || '').startsWith('TASK_') },
  {
    id: 'VALIDATIONS',
    label: 'Validations',
    match: (code) => /_(SUBMITTED|APPROVED|REJECTED|CHANGES_REQUESTED)$/.test(String(code || '')),
  },
  {
    id: 'QUOTES',
    label: 'Devis',
    match: (code) => {
      const c = String(code || '');
      return c.startsWith('QUOTE_') && !/_(SUBMITTED|APPROVED|REJECTED|CHANGES_REQUESTED)$/.test(c);
    },
  },
  {
    id: 'CRM',
    label: 'Prospects / Clients',
    match: (code) => /^(PROSPECT_|ORGANIZATION_|CLIENT_|CONTACT_|ORG_|STATUS_|NOTE_|COMMERCANT_)/.test(String(code || '')),
  },
  {
    id: 'INVOICING',
    label: 'Facturation',
    match: (code) => {
      const c = String(code || '');
      return /^(INVOICE_|CREDIT_NOTE_)/.test(c) && !/_(SUBMITTED|APPROVED|REJECTED|CHANGES_REQUESTED)$/.test(c);
    },
  },
  {
    id: 'OTHER',
    label: 'Autres',
    match: (code) => {
      const c = String(code || '');
      if (!c) return true;
      for (const f of NR2J_ACTIVITY_TYPE_FILTERS) {
        if (!f.id || f.id === 'OTHER') continue;
        if (f.match?.(c)) return false;
      }
      return true;
    },
  },
]);

/** Mapping Types d'actions (sidebar) → typeGroup filter id */
export const NR2J_ACTIVITY_TYPE_BREAKDOWN = Object.freeze([
  { id: 'TASKS', label: 'Tâches', color: 'bg-indigo-400' },
  { id: 'QUOTES', label: 'Devis', color: 'bg-emerald-400' },
  { id: 'CRM', label: 'Prospects / Clients', color: 'bg-sky-400' },
  { id: 'VALIDATIONS', label: 'Validations', color: 'bg-amber-400' },
  { id: 'INVOICING', label: 'Facturation', color: 'bg-rose-400' },
  { id: 'OTHER', label: 'Autres', color: 'bg-slate-400' },
]);

export const NR2J_ACTIVITY_PERIOD_PRESETS = Object.freeze([
  { id: 'today', label: 'Aujourd’hui' },
  { id: 'yesterday', label: 'Hier' },
  { id: '7d', label: '7 derniers jours' },
  { id: '30d', label: '30 derniers jours' },
  { id: 'custom', label: 'Personnalisée' },
]);

export const NR2J_ACTIVITY_PAGE_SIZE = 50;

const CUID_RE = /\b(c[a-z0-9]{20,})\b/gi;
const TECH_ID_RE = /\b(quoteId|userId|organizationId|entityId|taskId)\b[=:]?\s*[a-z0-9_-]*/gi;
const ENUM_IN_MESSAGE_RE = /\b(APPROVED|REJECTED|CHANGES_REQUESTED|PENDING|SUBMITTED|SENT)\b/g;
const SEED_NOISE_RE = /\(?\s*nr2j[-_]?ux[-_]?local[-_]?seed\s*\)?/gi;

export function nr2jActivityCode(row) {
  const action = row?.action ? String(row.action) : '';
  const type = row?.type ? String(row.type) : '';
  // Les seeds locaux stockent parfois le code dans `type` et laissent `action` vide.
  if (action && /^[A-Z][A-Z0-9_]+$/.test(action)) return action;
  if (type && /^[A-Z][A-Z0-9_]+$/.test(type)) return type;
  return action || type || '';
}

export function nr2jActivityActionLabel(rowOrCode) {
  const code = typeof rowOrCode === 'string' ? rowOrCode : nr2jActivityCode(rowOrCode);
  if (!code) return 'Action';
  if (NR2J_ACTIVITY_ACTION_LABELS[code]) return NR2J_ACTIVITY_ACTION_LABELS[code];
  return String(code)
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function nr2jActivityActorName(row) {
  const actor = row?.actor;
  if (!actor) return null;
  const name = [actor.prenom, actor.nom].filter(Boolean).join(' ').trim();
  return name || actor.email || null;
}

export function nr2jActivityClientName(row) {
  const org = row?.organization;
  if (!org) return null;
  const name = org.nomCommercial || org.raisonSociale || null;
  if (!name) return null;
  if (SEED_NOISE_RE.test(name)) return null;
  return name;
}

export function sanitizeNr2jActivityMessage(message, row) {
  let text = String(message || '').trim();
  if (!text) return '';

  text = text.replace(SEED_NOISE_RE, '');
  text = text.replace(CUID_RE, '').replace(TECH_ID_RE, '');
  text = text.replace(ENUM_IN_MESSAGE_RE, (token) => {
    const map = {
      APPROVED: 'validé',
      REJECTED: 'refusé',
      CHANGES_REQUESTED: 'modifications demandées',
      PENDING: 'en attente',
      SUBMITTED: 'soumis',
      SENT: 'envoyé',
    };
    return map[token] || token;
  });
  text = text
    .replace(/^Décision\s+/i, '')
    .replace(/\bQUOTE\b/g, 'Devis')
    .replace(/\bINVOICE\b/g, 'Facture')
    .replace(/\bCREDIT_NOTE\b/g, 'Avoir')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+[·•\-—]\s*$/, '')
    .replace(/^\s*[·•\-—]\s*/, '')
    .trim();

  if (!text || /^[A-Z0-9_]+$/.test(text)) return '';
  return text;
}

/**
 * Objet lisible sous le titre (document + client), sans répéter le libellé d'action.
 */
export function nr2jActivityDescription(row) {
  const client = nr2jActivityClientName(row);
  const doc = row?.documentNumber || row?.subjectLabel || null;

  if (doc && client && !String(doc).includes(client)) {
    return `${doc} — ${client}`;
  }
  if (doc) return String(doc);
  if (client) return client;

  let text = sanitizeNr2jActivityMessage(row?.message, row);
  const label = nr2jActivityActionLabel(row);
  if (text && text.toLowerCase().startsWith(label.toLowerCase())) {
    text = text.slice(label.length).replace(/^\s*[—\-·:]\s*/, '').trim();
  }
  text = text
    .replace(/^(validé|refusé|soumis|envoyé)\s*[—\-]?\s*/i, '')
    .replace(SEED_NOISE_RE, '')
    .trim();
  return text || '—';
}

export function nr2jActivityHref(row) {
  if (row?.href) return row.href;
  return null;
}

export function nr2jActivityTone(row) {
  const code = nr2jActivityCode(row);
  if (/REJECTED|REFUSED|DELETED|CANCELLED/.test(code)) {
    return { iconBg: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' };
  }
  if (/APPROVED|ACCEPTED|COMPLETED|CONVERTED|PAYMENT|ISSUED/.test(code) && !/CHANGES/.test(code)) {
    return { iconBg: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' };
  }
  if (/SUBMITTED|CHANGES_REQUESTED|PENDING|RESCHEDULED/.test(code)) {
    return { iconBg: 'bg-amber-100 text-amber-800', dot: 'bg-amber-500' };
  }
  if (/TASK_/.test(code)) {
    return { iconBg: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' };
  }
  if (/QUOTE_|INVOICE_|CREDIT_/.test(code)) {
    return { iconBg: 'bg-emerald-50 text-emerald-800', dot: 'bg-emerald-400' };
  }
  if (/PROSPECT_|CLIENT_|ORGANIZATION_|CONTACT_|ORG_/.test(code)) {
    return { iconBg: 'bg-sky-100 text-sky-700', dot: 'bg-sky-500' };
  }
  return { iconBg: 'bg-slate-100 text-slate-700', dot: 'bg-slate-400' };
}

export const NR2J_ACTIVITY_TZ = 'Europe/Paris';

/** YYYY-MM-DD en Europe/Paris. */
export function parisYmd(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return 'unknown';
  return new Intl.DateTimeFormat('en-CA', { timeZone: NR2J_ACTIVITY_TZ }).format(d);
}

export function addParisDaysYmd(ymd, days) {
  const [y, m, d] = String(ymd).split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + days, 12, 0, 0));
  return dt.toISOString().slice(0, 10);
}

export function dayKey(date) {
  return parisYmd(date);
}

export function isoDateLocal(date = new Date()) {
  return parisYmd(date);
}

export function periodRangeFromPreset(presetId, now = new Date()) {
  const to = parisYmd(now);
  if (presetId === 'today') {
    return { from: to, to, periodPreset: 'today' };
  }
  if (presetId === 'yesterday') {
    const y = addParisDaysYmd(to, -1);
    return { from: y, to: y, periodPreset: 'yesterday' };
  }
  if (presetId === '30d') {
    return { from: addParisDaysYmd(to, -29), to, periodPreset: '30d' };
  }
  if (presetId === '7d') {
    return { from: addParisDaysYmd(to, -6), to, periodPreset: '7d' };
  }
  return { from: to, to, periodPreset: 'custom' };
}

export function formatNr2jActivityDayLabel(isoOrDate, now = new Date()) {
  const key = dayKey(isoOrDate);
  if (key === 'unknown') return 'Date inconnue';
  const today = parisYmd(now);
  const yesterday = addParisDaysYmd(today, -1);
  const long = new Date(isoOrDate).toLocaleDateString('fr-FR', {
    timeZone: NR2J_ACTIVITY_TZ,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  if (key === today) return `Aujourd’hui — ${long}`;
  if (key === yesterday) return `Hier — ${long}`;
  return long.charAt(0).toUpperCase() + long.slice(1);
}

export function formatNr2jActivityTime(isoOrDate) {
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleTimeString('fr-FR', {
    timeZone: NR2J_ACTIVITY_TZ,
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function isDayGroupDefaultOpen(groupKey, itemCount, now = new Date()) {
  const today = parisYmd(now);
  const yesterday = addParisDaysYmd(today, -1);
  if (groupKey === today) return true;
  if (groupKey === yesterday) return itemCount <= 30;
  return false;
}

export function groupNr2jActivitiesByDay(rows, now = new Date()) {
  const groups = [];
  const index = new Map();
  for (const row of rows || []) {
    const key = dayKey(row.createdAt);
    if (!index.has(key)) {
      const group = {
        key,
        label: formatNr2jActivityDayLabel(row.createdAt, now),
        items: [],
      };
      index.set(key, group);
      groups.push(group);
    }
    index.get(key).items.push(row);
  }
  return groups.map((g) => ({
    ...g,
    count: g.items.length,
    defaultOpen: isDayGroupDefaultOpen(g.key, g.items.length, now),
  }));
}

export function emptyActivitySummary() {
  return {
    todayCount: 0,
    quotesApproved: 0,
    tasksCompleted: 0,
    changesRequested: 0,
    total: 0,
    typeBreakdown: [],
  };
}

/**
 * Synthèse KPI sur un périmètre (période / collaborateur / client).
 * Ne doit PAS être recalculée après un filtre de type d'action.
 */
export function summarizeNr2jActivityKpis(rows, now = new Date()) {
  const list = rows || [];
  const todayYmd = parisYmd(now);
  const today = list.filter((r) => parisYmd(r.createdAt) === todayYmd);
  const code = (r) => nr2jActivityCode(r);
  return {
    todayCount: today.length,
    quotesApproved: list.filter((r) => code(r) === 'QUOTE_APPROVED').length,
    tasksCompleted: list.filter((r) => code(r) === 'TASK_COMPLETED').length,
    changesRequested: list.filter((r) => /CHANGES_REQUESTED$/.test(code(r))).length,
    total: list.length,
  };
}

export function summarizeNr2jActivityTypes(rows) {
  const counts = Object.fromEntries(NR2J_ACTIVITY_TYPE_BREAKDOWN.map((t) => [t.id, 0]));
  for (const row of rows || []) {
    const code = nr2jActivityCode(row);
    const match = NR2J_ACTIVITY_TYPE_FILTERS.find((f) => f.id && f.id !== 'OTHER' && f.match?.(code));
    const id = match?.id || 'OTHER';
    counts[id] = (counts[id] || 0) + 1;
  }
  return NR2J_ACTIVITY_TYPE_BREAKDOWN
    .map((t) => ({ ...t, count: counts[t.id] || 0 }))
    .filter((t) => t.count > 0);
}

export function buildActivityCollaboratorOptions(rows) {
  const map = new Map();
  for (const row of rows || []) {
    if (!row.actorUserId || !row.actor) continue;
    if (map.has(row.actorUserId)) continue;
    map.set(row.actorUserId, {
      id: row.actorUserId,
      label: nr2jActivityActorName(row) || row.actor.email || 'Collaborateur',
    });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

export function buildActivityClientOptions(rows) {
  const map = new Map();
  for (const row of rows || []) {
    const id = row.organizationId || row.organization?.id;
    const label = nr2jActivityClientName(row);
    if (!id || !label) continue;
    if (map.has(id)) continue;
    map.set(id, { id, label });
  }
  return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, 'fr'));
}

export function activityMatchesTypeFilter(row, typeFilterId) {
  if (!typeFilterId) return true;
  const filter = NR2J_ACTIVITY_TYPE_FILTERS.find((f) => f.id === typeFilterId);
  if (!filter?.match) return true;
  return filter.match(nr2jActivityCode(row));
}

export function activityMatchesExactAction(row, actionExact) {
  if (!actionExact) return true;
  return nr2jActivityCode(row) === actionExact
    || (actionExact === 'CHANGES_REQUESTED' && /CHANGES_REQUESTED$/.test(nr2jActivityCode(row)));
}

export function activityMatchesKpiFilter(row, kpiId, now = new Date()) {
  if (!kpiId) return true;
  const code = nr2jActivityCode(row);
  if (kpiId === 'today') {
    return parisYmd(row.createdAt) === parisYmd(now);
  }
  if (kpiId === 'quotesApproved') return code === 'QUOTE_APPROVED';
  if (kpiId === 'tasksCompleted') return code === 'TASK_COMPLETED';
  if (kpiId === 'changesRequested') return /CHANGES_REQUESTED$/.test(code);
  return true;
}

/** Les filtres de lecture (type) ne doivent pas recalculer les KPI. */
export function kpisRemainStableAcrossTypeFilter(scopeRows, filteredRows, now = new Date()) {
  const a = summarizeNr2jActivityKpis(scopeRows, now);
  const b = summarizeNr2jActivityKpis(scopeRows, now);
  // volontairement compare synthèse scope vs elle-même (contrat)
  // et vérifie que le fil filtré peut différer
  return (
    a.todayCount === b.todayCount
    && a.quotesApproved === b.quotesApproved
    && a.tasksCompleted === b.tasksCompleted
    && a.changesRequested === b.changesRequested
    && (filteredRows?.length ?? 0) <= (scopeRows?.length ?? 0)
  );
}

export function defaultActivityFilters(now = new Date()) {
  const range = periodRangeFromPreset('7d', now);
  return {
    actorUserId: '',
    organizationId: '',
    typeGroup: '',
    actionExact: '',
    kpiId: '',
    periodPreset: range.periodPreset,
    from: range.from,
    to: range.to,
  };
}

export function activityFiltersAreActive(filters, now = new Date()) {
  const defaults = defaultActivityFilters(now);
  return Boolean(
    filters.actorUserId
    || filters.organizationId
    || filters.typeGroup
    || filters.actionExact
    || filters.kpiId
    || filters.from !== defaults.from
    || filters.to !== defaults.to
    || (filters.periodPreset && filters.periodPreset !== '7d'),
  );
}
